import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, ArrowRight, CheckCircle2, Mail, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import awloLogo from '../assets/AWLO1.png';
import authImg from '../assets/authImg.png';

// --- VARIANTES D'ANIMATION ---
const kenBurnsEffect = {
  initial: { scale: 1, x: '0%', y: '0%' },
  animate: { 
    scale: 1.1, 
    x: '-2%', 
    y: '-1%',
    transition: { duration: 20, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" }
  }
};

const logoFadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { delay: 0.5, duration: 1 } }
};

const formContainerVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { duration: 0.4, ease: "easeOut", when: "beforeChildren", staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 20 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
};

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    if (!validateEmail(email.trim())) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      setSuccess(true);
      toast({
        title: 'Email envoyé',
        description: 'Un lien de réinitialisation a été envoyé à votre adresse email.',
        duration: 4000
      });
    } catch (err) {
      const errorMessage = err.message || 'Une erreur est survenue. Veuillez réessayer.';
      setError(errorMessage);
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
        duration: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-4 sm:p-8 overflow-hidden font-sans">
      
      {/* --- BACKGROUND --- */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${authImg})` }}
          variants={kenBurnsEffect} initial="initial" animate="animate"
        />
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      </div>

      {/* Logo flottant au-dessus du background */}
      <motion.div 
        variants={logoFadeIn} initial="initial" animate="animate"
        className="absolute top-[30%] left-[20%] md:left-[25%] lg:left-[32%] z-50 hidden md:block pointer-events-auto"
      >
        <Link to="/" className="block hover:opacity-90 transition-opacity focus:outline-none cursor-pointer">
          <img src={awloLogo} alt="AWLO" className="h-24 lg:h-32 w-auto object-contain drop-shadow-2xl brightness-110" />
        </Link>
      </motion.div>

      {/* --- CONTENT LAYOUT --- */}
      <div className="relative z-10 w-full max-w-6xl grid lg:grid-cols-2 gap-12 items-center">
        
        {/* --- LEFT: SLOGAN --- */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="hidden lg:flex flex-col justify-center items-start space-y-6 pl-10"
        >
          <div className="text-white space-y-2 mt-32">
            <h2 className="text-4xl font-bold leading-tight">Récupération<br/>de compte.</h2>
            <p className="text-white/70 text-lg max-w-md font-light">
              Nous allons vous aider à retrouver l'accès à votre espace en toute sécurité.
            </p>
          </div>
        </motion.div>

        {/* --- RIGHT: FORM --- */}
        <div className="flex justify-center lg:justify-end pr-0 lg:pr-10">
          <motion.div 
            variants={formContainerVariants} initial="hidden" animate="visible"
            className="w-full max-w-[420px] bg-black/20 backdrop-blur-md rounded-3xl border border-white/10 p-8 shadow-2xl"
          >
            
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-6">
              <Link to="/" className="block hover:opacity-90 transition-opacity focus:outline-none cursor-pointer pointer-events-auto" aria-label="Retour à l'accueil">
                <img src={awloLogo} alt="AWLO" className="h-14 w-auto object-contain drop-shadow-lg" />
              </Link>
            </div>

            {/* Bouton Retour (discret en haut) */}
            {!success && (
                <motion.div className="mb-4" variants={itemVariants}>
                    <button
                        onClick={handleNavigateToLogin}
                        className="flex items-center gap-2 text-white/60 hover:text-white transition-colors group text-sm font-medium"
                    >
                        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                        Retour
                    </button>
                </motion.div>
            )}

            <AnimatePresence mode="wait">
              {!success ? (
                <motion.div
                  key="form-content"
                  initial="hidden" animate="visible" exit="exit"
                  className="space-y-6"
                >
                  {/* En-tête */}
                  <motion.div variants={itemVariants}>
                    <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">Mot de passe oublié</h1>
                    <p className="text-white/60 text-sm">Entrez votre email pour recevoir un lien de réinitialisation</p>
                  </motion.div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email */}
                    <motion.div variants={itemVariants} className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-semibold text-white/80 uppercase tracking-wider ml-1">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="votre@email.com"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setError(null);
                          }}
                          className={`h-12 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/40 pl-10 pr-4 focus:ring-2 focus:ring-[#FF9F68] focus:border-transparent focus:bg-white/20 transition-all ${
                            error ? 'border-red-400 focus:border-red-400 focus:ring-red-400/50' : ''
                          }`}
                          required
                        />
                      </div>
                      {error && (
                        <p className="text-xs text-red-300 ml-1 font-medium">{error}</p>
                      )}
                    </motion.div>

                    {/* Bouton d'envoi */}
                    <motion.div variants={itemVariants}>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 bg-[#FF9F68] hover:bg-[#E67E4F] text-white font-bold rounded-xl shadow-lg shadow-orange-900/20 transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Envoi...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Envoyer le lien <ArrowRight className="h-5 w-5" />
                          </span>
                        )}
                      </Button>
                    </motion.div>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="success-content"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                  className="text-center py-4"
                >
                  {/* Icône de succès */}
                  <motion.div
                    className="mb-6 flex justify-center"
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 }}
                  >
                    <div className="relative">
                      <CheckCircle2 className="h-20 w-20 text-green-400" />
                      <motion.div
                        className="absolute inset-0 rounded-full bg-green-400/20"
                        initial={{ scale: 0 }} animate={{ scale: 2, opacity: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
                      />
                    </div>
                  </motion.div>

                  {/* Titre de succès */}
                  <motion.h2
                    className="text-2xl font-bold text-white mb-4"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                  >
                    Email envoyé !
                  </motion.h2>

                  {/* Message de confirmation */}
                  <motion.div
                    className="space-y-4 mb-8"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                  >
                    <p className="text-white/80 text-sm leading-relaxed">
                      Si un compte existe avec l'adresse <span className="font-semibold text-white">{email}</span>, 
                      vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
                    </p>
                    <p className="text-xs text-white/50">
                      Pensez à vérifier vos spams si vous ne voyez pas l'email.
                    </p>
                  </motion.div>

                  {/* Bouton retour à la connexion */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                  >
                    <Button
                      onClick={handleNavigateToLogin}
                      className="w-full h-12 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 transition-all flex items-center justify-center gap-2"
                    >
                      Retour à la connexion
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

          </motion.div>
        </div>
      </div>
      
      {/* Copyright discret */}
      <div className="absolute bottom-4 left-0 w-full text-center z-10 pointer-events-none">
         <p className="text-white/20 text-xs font-medium">© 2025 AWLO</p>
      </div>

    </div>
  );
};

export default ForgotPasswordPage;